import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  MousePointerClick,
  Package,
  GitFork,
  Brain,
  Rocket,
  ListChecks,
  Users,
  BookOpen,
  Volume2,
  Shield,
  MessageSquare,
  LifeBuoy,
  Target,
  BarChart3,
  Crosshair,
  Zap,
  TrendingDown,
  Smile,
  TrendingUp,
  User,
  Bot,
  CheckCheck,
  FileText,
  HelpCircle,
  Contact,
  Star,
  Link,
  Mail,
  RefreshCw,
  Globe,
  Handshake,
  Bell,
  ScrollText,
  NotebookPen,
  Search,
  Compass,
  Image,
  Video,
  ClipboardList,
  GalleryHorizontalEnd,
  ShoppingBag,
  ShoppingCart,
  Heart,
  GraduationCap,
  Monitor,
  Building2,
  Wallet,
  Code,
  MessageCircle,
} from "lucide-react";
import integrationImages from "../assets/PowerfulIntegrations";
import BRAND from "../theme/talktokam";
import languageImg from "../assets/images/language.webp";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const GlassCard = ({ children, sx, glowColor }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    style={{ height: "100%" }}
  >
    <Box
      sx={{
        p: 4,
        borderRadius: 3,
        bgcolor: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.07)",
        height: "100%",
        transition: "all 0.3s",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: `${glowColor || BRAND.cyan}66`,
          boxShadow: `0 12px 40px ${glowColor || BRAND.cyan}1a`,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  </motion.div>
);

const SectionHeading = ({ emoji, title, sub }) => (
  <motion.div {...fadeUp}>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: { xs: "1.6rem", md: "2.2rem" },
        background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`,
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        mb: 1.5,
      }}
    >
      {emoji} {title}
    </Typography>
    {sub && (
      <Typography
        sx={{
          fontSize: "1rem",
          color: "#94a3b8",
          maxWidth: 650,
          mx: 0,
          mb: 4,
        }}
      >
        {sub}
      </Typography>
    )}
  </motion.div>
);

const FeatureRow = ({ items, cols = { xs: 12, sm: 6, md: 4 } }) => (
  <Grid container spacing={3}>
    {items.map((item, i) => (
      <Grid item {...cols} key={i} sx={{ display: "flex" }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.1 }}
          style={{ width: "100%", height: "100%" }}
        >
          <GlassCard glowColor={item.color}>
            {item.icon &&
              (typeof item.icon === "string" ? (
                <Typography sx={{ fontSize: "2rem", mb: 1, lineHeight: 1 }}>
                  {item.icon}
                </Typography>
              ) : (
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(56,189,248,0.08)",
                    border: "1px solid rgba(56,189,248,0.15)",
                    mb: 1.5,
                  }}
                >
                  <item.icon size={22} strokeWidth={1.5} color="#38bdf8" />
                </Box>
              ))}
            <Typography
              sx={{ fontWeight: 700, fontSize: "1rem", color: "#fff", mb: 0.5 }}
            >
              {item.title}
            </Typography>
            <Typography
              sx={{ fontSize: "0.85rem", color: "#94a3b8", lineHeight: 1.6 }}
            >
              {item.desc}
            </Typography>
          </GlassCard>
        </motion.div>
      </Grid>
    ))}
  </Grid>
);

const HeroSection = () => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        position: "relative",
        textAlign: "center",
        py: { xs: 8, md: 10 },
        pt: { xs: 17, md: 19 },
        overflow: "hidden",
        background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          top: "5%",
          left: "-5%",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          bottom: "-10%",
          right: "-10%",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          top: "50%",
          right: "30%",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`,
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.8rem" },
              background: "linear-gradient(135deg, #38bdf8, #a855f7)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 2,
              lineHeight: 1.15,
            }}
          >
            AI Website Chatbot to Capture Leads, Engage Visitors & Automate
            Customer Support
          </Typography>
          <Typography
            sx={{
              fontSize: "1.05rem",
              color: "#94a3b8",
              mb: 4,
              maxWidth: 680,
              mx: "auto",
            }}
          >
            Build intelligent AI-powered chatbots for your website without
            coding. Capture leads, answer customer queries instantly, and
            provide 24/7 support while increasing conversions.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              mb: 4,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/signup")}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                px: 4,
                py: 1.5,
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" },
                fontSize: "1rem",
              }}
            >
              Start Free Trial <ArrowForwardIcon sx={{ ml: 1, fontSize: 18 }} />
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/contact")}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderColor: "rgba(255,255,255,0.3)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.05)",
                },
              }}
            >
              Book a Demo
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, md: 3 },
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {[
              "14 Days Free Trial",
              "No Credit Card Required",
              "Setup in Minutes",
            ].map((b, i) => (
              <Box
                key={i}
                sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
              >
                <CheckCircleIcon sx={{ color: "#10b981", fontSize: 18 }} />
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#cbd5e1",
                    fontWeight: 500,
                  }}
                >
                  {b}
                </Typography>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

const PowerfulIntegrations = () => {
  const rows = [];
  for (let i = 0; i < integrationImages.length; i += 16) {
    rows.push(integrationImages.slice(i, i + 16));
  }
  return (
      <Box
        sx={{
          py: { xs: 5, md: 7 },
          background: "linear-gradient(180deg, #0a0e27, #050816)",
          overflow: "hidden",
        }}
      >
      <Container maxWidth="lg" sx={{ textAlign: "center", mb: 6 }}>
        <motion.div {...fadeUp}>
          <SectionHeading
            emoji=""
            title="Powerful Integrations That Work With Your Business"
            sub="TalkToKam seamlessly integrates with your favorite platforms, allowing AI-powered conversations, customer data, and workflows to stay connected in one place."
          />
        </motion.div>
      </Container>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            width: 120,
            zIndex: 2,
            pointerEvents: "none",
          },
          "&::before": {
            left: 0,
            background: "linear-gradient(90deg, #050816 0%, transparent 100%)",
          },
          "&::after": {
            right: 0,
            background: "linear-gradient(270deg, #050816 0%, transparent 100%)",
          },
        }}
      >
        {rows.map((row, rowIdx) => (
          <Box
            key={rowIdx}
            className={`marquee-track${rowIdx === 1 ? " reverse" : ""}`}
            sx={{ mb: 2.5 }}
          >
            {[...row, ...row].map((img, i) => (
              <Box
                key={i}
                sx={{
                  flexShrink: 0,
                  width: 140,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1.5,
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: "rgba(56,189,248,0.4)",
                    bgcolor: "rgba(255,255,255,0.07)",
                  },
                }}
              >
                <Box
                  component="img"
                  src={img.src}
                  alt={img.name}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    filter: "brightness(1) contrast(1.1)",
                  }}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const WhatIsSection = () => (
  <Box
    sx={{
      py: { xs: 5, md: 7 },
      background: "#050816",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <motion.div
      animate={{ y: [0, -30, 0], scale: [1, 1.05, 1] }}
      transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      style={{
        position: "absolute",
        top: "10%",
        left: "5%",
        width: 400,
        height: 400,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${BRAND.cyan}30 0%, transparent 70%)`,
        filter: "blur(60px)",
        pointerEvents: "none",
      }}
    />
    <Container maxWidth="lg">
      <Grid container spacing={6} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <motion.div {...fadeUp} style={{ width: "100%" }}>
            <SectionHeading title="Your 24/7 AI-Powered Virtual Assistant" />
            <Typography
              sx={{
                fontSize: "1rem",
                color: "#94a3b8",
                lineHeight: 1.8,
                mb: 3,
              }}
            >
              TalkToKam Website Bot engages visitors instantly, answers
              questions, qualifies leads, schedules appointments, and provides
              support without human intervention.
            </Typography>
            <Typography
              sx={{
                fontSize: "1rem",
                color: "#94a3b8",
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              Whether visitors arrive during business hours or late at night,
              your chatbot is always available to assist them.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[
                { icon: Crosshair, text: "Never miss a lead" },
                { icon: Zap, text: "Instant customer responses" },
                { icon: TrendingDown, text: "Reduce support workload" },
                { icon: Smile, text: "Improve customer experience" },
                { icon: TrendingUp, text: "Increase conversion rates" },
              ].map((b, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    transition: "all 0.3s",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.06)",
                      borderColor: `${BRAND.cyan}44`,
                      transform: "translateX(6px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `rgba(56,189,248,0.1)`,
                      border: `1px solid rgba(56,189,248,0.15)`,
                    }}
                  >
                    <b.icon size={18} strokeWidth={1.5} color="#38bdf8" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      color: "#cbd5e1",
                      fontWeight: 500,
                    }}
                  >
                    {b.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ width: "100%", height: "100%" }}
          >
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                minHeight: { md: 670 },
                background:
                  "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                border: "1px solid rgba(255,255,255,0.08)",
                textAlign: "center",
                position: { md: "relative" },
                isolation: { md: "isolate" },
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "-30%",
                  right: "-20%",
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${BRAND.purple}20 0%, transparent 70%)`,
                  filter: "blur(40px)",
                  pointerEvents: "none",
                }}
              />
              <Box
                component="svg"
                viewBox="0 0 500 650"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 0,
                  opacity: 0.6,
                  pointerEvents: "none",
                  display: { xs: "none", md: "block" },
                }}
              >
                <defs>
                  <filter id="roadGlow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <path
                  d="M 200 105 C 420 105, 420 317, 250 317 C 160 317, 160 475, 200 480"
                  stroke="#38bdf8"
                  strokeWidth="3"
                  strokeDasharray="8 12"
                  fill="none"
                  strokeLinecap="round"
                  filter="url(#roadGlow)"
                  className="road-path"
                />
              </Box>
              <Box
                sx={{
                  position: { md: "absolute" },
                  width: { md: 260 },
                  top: { md: 25 },
                  left: { md: 28 },
                  zIndex: 1,
                  p: 3,
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(18px)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow:
                    "0 8px 32px rgba(0,0,0,0.15), inset 0 1px 1px rgba(255,255,255,0.2)",
                  transition: "0.4s",
                  "&:hover": {
                    transform: { md: "translateY(-6px)" },
                    boxShadow: { md: "0 20px 50px rgba(0,212,255,0.25)" },
                  },
                  textAlign: "left",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(56,189,248,0.08)",
                    border: "1px solid rgba(56,189,248,0.15)",
                    mb: 1.5,
                  }}
                >
                  <User size={24} strokeWidth={1.5} color="#38bdf8" />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: "#fff",
                    mb: 0.5,
                  }}
                >
                  Visitor
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#d7d7d7",
                    lineHeight: 1.6,
                  }}
                >
                  Types a question in the widget and starts a conversation.
                </Typography>
              </Box>
              <Box
                sx={{
                  position: { md: "absolute" },
                  width: { md: 260 },
                  top: { md: "50%" },
                  right: { md: 28 },
                  transform: { md: "translateY(-50%)" },
                  zIndex: 1,
                  p: 3,
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(18px)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow:
                    "0 8px 32px rgba(0,0,0,0.15), inset 0 1px 1px rgba(255,255,255,0.2)",
                  transition: "0.4s",
                  "&:hover": {
                    transform: { md: "translateY(calc(-50% - 6px))" },
                    boxShadow: { md: "0 20px 50px rgba(0,212,255,0.25)" },
                  },
                  textAlign: "left",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(168,85,247,0.08)",
                    border: "1px solid rgba(168,85,247,0.15)",
                    mb: 1.5,
                  }}
                >
                  <Bot size={24} strokeWidth={1.5} color="#a855f7" />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: "#fff",
                    mb: 0.5,
                  }}
                >
                  AI Engine
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#d7d7d7",
                    lineHeight: 1.6,
                  }}
                >
                  Analyzes intent, context, and knowledge sources instantly.
                </Typography>
              </Box>
              <Box
                sx={{
                  position: { md: "absolute" },
                  width: { md: 260 },
                  bottom: { md: 25 },
                  left: { md: 28 },
                  zIndex: 1,
                  p: 3,
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(18px)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow:
                    "0 8px 32px rgba(0,0,0,0.15), inset 0 1px 1px rgba(255,255,255,0.2)",
                  transition: "0.4s",
                  "&:hover": {
                    transform: { md: "translateY(-6px)" },
                    boxShadow: { md: "0 20px 50px rgba(0,212,255,0.25)" },
                  },
                  textAlign: "left",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(16,185,129,0.08)",
                    border: "1px solid rgba(16,185,129,0.15)",
                    mb: 1.5,
                  }}
                >
                  <CheckCheck size={24} strokeWidth={1.5} color="#10b981" />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: "#fff",
                    mb: 0.5,
                  }}
                >
                  Solution
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#d7d7d7",
                    lineHeight: 1.6,
                  }}
                >
                  Delivers the most relevant answer within seconds.
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const ChatMockup = () => {
  const [step, setStep] = useState(0);
  const containerRef = useRef(null);
  const inView = useInView(containerRef, { once: false, margin: "-100px" });

  useEffect(() => {
    if (!inView) return;
    const timer = setTimeout(() => {
      setStep((p) => (p >= steps.length - 1 ? 0 : p + 1));
    }, 2500);
    return () => clearTimeout(timer);
  }, [step, inView]);

  const steps = [
    {
      bot: "👋 Hi! I'm your AI assistant. How can I help you today?",
      button: "🔑 How do I reset my password?",
    },
    {
      user: "🔑 How do I reset my password?",
      bot: "I can help with that! What's your registered email?",
      button: "📧 john@example.com",
    },
    {
      user: "📧 john@example.com",
      bot: "A password reset link has been sent to your email. Check your inbox!",
      button: "📋 I'd like to request a refund",
    },
    {
      user: "📋 I'd like to request a refund",
      bot: "Sure! I'll create a refund ticket for you. Our team will review it within 24 hours.",
      button: "🕐 What are your business hours?",
    },
    {
      user: "🕐 What are your business hours?",
      bot: "We're available Mon-Fri 9AM-6PM EST, and our AI works 24/7!",
      button: "🙏 Thank you!",
    },
    {
      user: "🙏 Thank you!",
      bot: "You're welcome! Feel free to ask anytime. 😊",
      button: null,
    },
  ];

  const isCurrent = (i) => i === step;

  return (
    <Box
      ref={containerRef}
      sx={{
        p: 3,
        borderRadius: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(160deg, rgba(168,85,247,0.1) 0%, rgba(99,102,241,0.06) 100%)",
        border: "1px solid rgba(168,85,247,0.2)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2,
          pb: 2,
          flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          T
        </Box>
        <Box>
          <Typography
            sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff" }}
          >
            TalkToKam Bot
          </Typography>
          <Typography sx={{ fontSize: "0.65rem", color: "#10b981" }}>
            ● Online
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {steps.slice(0, step + 1).map((s, i) => (
          <React.Fragment key={i}>
            {s.user && (
              <motion.div
                initial={isCurrent(i) ? { opacity: 0, x: 20 } : undefined}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.5,
                  delay: isCurrent(i) ? 0 : 0,
                  ease: "easeOut",
                }}
                style={{ alignSelf: "flex-end", maxWidth: "80%" }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.purple})`,
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", color: "#fff" }}>
                    {s.user}
                  </Typography>
                </Box>
              </motion.div>
            )}
            <motion.div
              initial={isCurrent(i) ? { opacity: 0, y: 15 } : undefined}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: isCurrent(i) ? 0.6 : 0,
                ease: "easeOut",
              }}
              style={{ alignSelf: "flex-start", maxWidth: "80%" }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.08)",
                }}
              >
                <Typography sx={{ fontSize: "0.8rem", color: "#e0e7ff" }}>
                  {s.bot}
                </Typography>
              </Box>
            </motion.div>
            {s.button && isCurrent(i) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8, ease: "easeOut" }}
                onClick={() =>
                  setStep((p) => (p >= steps.length - 1 ? 0 : p + 1))
                }
                style={{ alignSelf: "flex-end", maxWidth: "80%" }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.purple})`,
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", color: "#fff" }}>
                    {s.button}
                  </Typography>
                </Box>
              </motion.div>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

const AnalyticsMockup = () => (
  <Box
    sx={{
      p: 3,
      borderRadius: 3,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      background:
        "linear-gradient(160deg, rgba(99,102,241,0.1) 0%, rgba(56,189,248,0.06) 100%)",
      border: "1px solid rgba(99,102,241,0.2)",
    }}
  >
    <Typography
      sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#fff", mb: 1 }}
    >
      {"\uD83D\uDCCA"} Analytics Overview
    </Typography>

    {[
      {
        label: 'Chat Volume',
        color: BRAND.cyan,
        data: [65, 85, 50, 95, 75, 45, 35],
      },
      {
        label: 'Leads Captured',
        color: BRAND.primary,
        data: [20, 32, 15, 40, 28, 12, 8],
      },
      {
        label: 'Conversion Rate (%)',
        color: "#10b981",
        data: [30, 38, 30, 42, 37, 27, 23],
      },
    ].map((chart, ci) => (
      <Box key={ci} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, flex: 1 }}>
          {chart.data.map((h, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "65%",
                  height: h * 2,
                  borderRadius: "4px 4px 0 0",
                  background: `linear-gradient(180deg, ${chart.color}, ${chart.color}66)`,
                  minHeight: 6,
                }}
              />
              <Typography
                sx={{ fontSize: "0.5rem", color: "#64748b", mt: 0.3 }}
              >
                {["M", "T", "W", "T", "F", "S", "S"][i]}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 600,
            color: "#94a3b8",
            mt: 0.5,
            textAlign: "center",
          }}
        >
          {chart.label}
        </Typography>
      </Box>
    ))}

    <Box sx={{ display: "flex", gap: 2, mt: "auto", pt: 1 }}>
      {[
        { label: "Total Chats", value: "1,247", color: BRAND.cyan },
        { label: "Leads Captured", value: "384", color: BRAND.primary },
        { label: "Conversion", value: "31%", color: "#10b981" },
      ].map((k, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.04)",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.6rem", color: "#64748b", mb: 0.2 }}>
            {k.label}
          </Typography>
          <Typography
            sx={{ fontSize: "1.1rem", fontWeight: 800, color: k.color }}
          >
            {k.value}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const faqData = [
  {
    q: "What is a Website Chatbot?",
    a: "A website chatbot is an AI-powered virtual assistant that lives on your website. It engages visitors in real-time conversations, answers questions, captures leads, and provides support 24/7 without human intervention.",
  },
  {
    q: "How does AI Website Bot work?",
    a: "Our AI Website Bot uses natural language processing to understand visitor messages. It can be trained on your FAQs, documents, and website content to provide accurate, context-aware responses automatically.",
  },
  {
    q: "Can I customize chatbot flows?",
    a: "Yes! Our visual drag-and-drop flow builder lets you create custom conversation paths, add conditional logic, and design multi-step workflows without any coding knowledge.",
  },
  {
    q: "Can I train the chatbot using my website?",
    a: "Absolutely. You can upload PDFs, documents, or let our AI crawl your website to automatically train your chatbot with your business knowledge and content.",
  },
  {
    q: "Does it support live chat?",
    a: "Yes. When the AI cannot handle a query, conversations can be seamlessly transferred to your human support team with full chat history and context preserved.",
  },
  {
    q: "Is coding required?",
    a: "Not at all. TalkToKam is a no-code platform. You can build, customize, and deploy your chatbot using our visual tools without writing a single line of code.",
  },
  {
    q: "Can I integrate with CRM tools?",
    a: "Yes, we integrate with popular CRMs like HubSpot, Salesforce, Zoho, and more. You can also connect via Zapier, webhooks, or Google Sheets sync.",
  },
  {
    q: "How quickly can I launch?",
    a: "You can create and deploy your first chatbot within minutes. Our pre-built templates and intuitive builder make it fast and easy to get started.",
  },
];

const Features = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <HeroSection />
      <PowerfulIntegrations />
      <WhatIsSection />

      <Box
        sx={{
          py: { xs: 5, md: 7 },
          background: "linear-gradient(180deg, #050816, #0a0e27)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "left", mb: 4 }}>
            <motion.div {...fadeUp}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.6rem", md: "2.2rem" },
                  background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1.5,
                }}
              >
                Build Powerful Chatbots Without Coding
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  color: "#94a3b8",
                  maxWidth: 650,
                  mx: 0,
                  mb: 2,
                }}
              >
                Create chatbot journeys using our visual drag-and-drop builder.
                Design conversations exactly how you want without technical
                expertise.
              </Typography>
            </motion.div>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                icon: MousePointerClick,
                title: "Drag & Drop Flow Builder",
                desc: "Build conversations visually by dragging and connecting nodes.",
              },
              {
                icon: Package,
                title: "Pre-Built Templates",
                desc: "Start fast with ready-made templates for any use case.",
              },
              {
                icon: GitFork,
                title: "Custom Chat Paths",
                desc: "Design unique conversation flows for different scenarios.",
              },
              {
                icon: Brain,
                title: "Conditional Logic",
                desc: "Route users based on their answers and behavior.",
              },
              {
                icon: Rocket,
                title: "Quick Deployment",
                desc: "Publish your chatbot with a single click or embed snippet.",
              },
              {
                icon: ListChecks,
                title: "Multi-Step Workflows",
                desc: "Create complex flows with forms, appointments, and more.",
              },
              {
                icon: Users,
                title: "Team Management",
                desc: "Admin can transfer chats to team members and assign roles for collaboration.",
              },
              {
                icon: BookOpen,
                title: "Knowledge Base",
                desc: "Upload documents or crawl your website — AI answers from your custom knowledge base.",
              },
              {
                icon: Volume2,
                title: "Text to Speech",
                desc: "Convert bot responses to natural-sounding speech for voice interactions.",
              },
              {
                icon: Shield,
                title: "Two-Factor Authentication",
                desc: "Add an extra layer of security for admin access and sensitive actions.",
              },
              {
                icon: MessageSquare,
                title: "Canned Response",
                desc: "Save and reuse pre-written replies for common questions.",
              },
              {
                icon: LifeBuoy,
                title: "Help Center",
                desc: "Provide instant self-help resources and guides within the chat.",
              },
              {
                icon: Users,
                title: "Users",
                desc: "Track user interactions and message history across your chatbot.",
              },
              {
                icon: Target,
                title: "Leads",
                desc: "Monitor and manage leads captured from chatbot conversations.",
              },
              {
                icon: BarChart3,
                title: "Analytics",
                desc: "Real-time dashboard with conversation insights and reports.",
              },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <GlassCard>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(56,189,248,0.08)",
                      border: "1px solid rgba(56,189,248,0.15)",
                      mb: 1.5,
                      transition: "0.3s",
                      "&:hover": {
                        bgcolor: "rgba(56,189,248,0.15)",
                        borderColor: "rgba(56,189,248,0.3)",
                      },
                    }}
                  >
                    <item.icon size={22} strokeWidth={1.5} color="#38bdf8" />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: "#fff",
                      mb: 0.5,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "#94a3b8",
                      lineHeight: 1.6,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 5, md: 7 },
          background: "linear-gradient(180deg, #0a0e27, #050816)",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="stretch">
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <Box sx={{ width: "100%" }}>
                <SectionHeading
                  title="Turn Website Visitors Into Qualified Leads"
                  sub="Capture leads automatically through engaging conversational experiences."
                />
                <FeatureRow
                  items={[
                    {
                      icon: FileText,
                      title: "Lead Capture Forms",
                      desc: "Collect visitor information through conversational forms.",
                    },
                    {
                      icon: HelpCircle,
                      title: "Qualification Questions",
                      desc: "Ask targeted questions to qualify leads.",
                    },
                    {
                      icon: Contact,
                      title: "Contact Collection",
                      desc: "Auto-capture names, emails, phone numbers.",
                    },
                    {
                      icon: Star,
                      title: "Lead Scoring",
                      desc: "Score and prioritize leads automatically.",
                    },
                    {
                      icon: Link,
                      title: "CRM Integration",
                      desc: "Push leads directly to\nyour CRM.",
                    },
                    {
                      icon: Mail,
                      title: "Automated Follow-Ups",
                      desc: "Send auto-email sequences to nurture leads.",
                    },
                  ]}
                  cols={{ xs: 12, sm: 6 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                style={{ width: "100%", height: "100%" }}
              >
                <AnalyticsMockup />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 5, md: 7 }, background: "#050816" }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="stretch">
            <Grid item xs={12} md={5} sx={{ display: "flex" }}>
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                style={{ width: "100%", height: "100%" }}
              >
                <ChatMockup />
              </motion.div>
            </Grid>
            <Grid item xs={12} md={7} sx={{ display: "flex" }}>
              <Box sx={{ width: "100%" }}>
                <SectionHeading
                  title="Provide Instant Customer Support 24/7"
                  sub="Automate frequently asked questions and support requests while reducing response times."
                />
                <FeatureRow
                  items={[
                    {
                      icon: HelpCircle,
                      title: "FAQ Automation",
                      desc: "Auto-answer common questions from your knowledge base.",
                    },
                    {
                      icon: BookOpen,
                      title: "Knowledge Base Responses",
                      desc: "Pull answers from your knowledge base.",
                    },
                    {
                      icon: FileText,
                      title: "Ticket Creation",
                      desc: "Auto-create support tickets for complex issues.",
                    },
                    {
                      icon: RefreshCw,
                      title: "Support Routing",
                      desc: "Route to the right team or department.",
                    },
                    {
                      icon: Zap,
                      title: "Instant Answers",
                      desc: "Respond within milliseconds, not minutes.",
                    },
                    {
                      icon: Globe,
                      title: "Multi-Language Support",
                      desc: "Serve global customers in 50+ languages with auto-detection.",
                    },
                  ]}
                  cols={{ xs: 12, sm: 6 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 5, md: 7 },
          background: "linear-gradient(180deg, #0a0e27, #050816)",
        }}
      >
        <Container maxWidth="lg">
          <SectionHeading
            title="Switch to Human Agents When Needed"
            sub="Allow AI to handle repetitive conversations and seamlessly transfer complex queries to your support team."
          />
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {[
              {
                icon: Handshake,
                title: "Human Takeover",
                desc: "Seamless handoff from AI to live agent.",
              },
              {
                icon: Bell,
                title: "Real-Time Notifications",
                desc: "Agents get instant alerts on incoming transfers.",
              },
              {
                icon: User,
                title: "Agent Assignment",
                desc: "Auto-assign to available team members.",
              },
              {
                icon: ScrollText,
                title: "Chat History Access",
                desc: "Agents see full conversation history.",
              },
              {
                icon: NotebookPen,
                title: "Internal Notes",
                desc: "Add private notes visible only to the team.",
              },
              {
                icon: Users,
                title: "Team Collaboration",
                desc: "Multiple agents can handle conversations together.",
              },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <GlassCard>
                  {item.icon &&
                    (typeof item.icon === "string" ? (
                      <Typography
                        sx={{ fontSize: "2rem", mb: 1, lineHeight: 1 }}
                      >
                        {item.icon}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(56,189,248,0.08)",
                          border: "1px solid rgba(56,189,248,0.15)",
                          mb: 1.5,
                          transition: "0.3s",
                          "&:hover": {
                            bgcolor: "rgba(56,189,248,0.15)",
                            borderColor: "rgba(56,189,248,0.3)",
                          },
                        }}
                      >
                        <item.icon
                          size={22}
                          strokeWidth={1.5}
                          color="#38bdf8"
                        />
                      </Box>
                    ))}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1rem",
                      color: "#fff",
                      mb: 0.5,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "#94a3b8",
                      lineHeight: 1.6,
                    }}
                  >
                    {item.desc}
                  </Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 5, md: 7 }, background: "#050816" }}>
        <Container maxWidth="lg">
          <SectionHeading
            title="Communicate With Customers in Their Language"
            sub="Support visitors from around the world with multilingual chatbot experiences."
          />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Box
              component="img"
              src={languageImg}
              alt="Global Language Support"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 4,
                mt: 2,
              }}
            />
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 5, md: 7 },
          background: "linear-gradient(180deg, #0a0e27, #050816)",
        }}
      >
        <Container maxWidth="lg">
          <SectionHeading
            title="Create Engaging Conversations"
            sub="Enhance chatbot interactions with rich media and interactive elements."
          />
          <FeatureRow
            items={[
              {
                icon: Image,
                title: "Images",
                desc: "Display product images and visuals.",
              },
              {
                icon: Video,
                title: "Videos",
                desc: "Embed product demos and video tutorials directly inside conversations.",
              },
              {
                icon: FileText,
                title: "PDFs",
                desc: "Share documents and brochures.",
              },
              {
                icon: MousePointerClick,
                title: "Buttons",
                desc: "Add clickable CTA buttons.",
              },
              {
                icon: Zap,
                title: "Quick Replies",
                desc: "Speed up conversations with quick options.",
              },
              {
                icon: ClipboardList,
                title: "Forms",
                desc: "Capture data through in-chat forms.",
              },
              {
                icon: GalleryHorizontalEnd,
                title: "Carousels",
                desc: "Showcase multiple products or services.",
              },
              {
                icon: ShoppingBag,
                title: "Product Cards",
                desc: "Display product details with images and prices.",
              },
            ]}
            cols={{ xs: 12, sm: 6, md: 3 }}
          />
        </Container>
      </Box>

      <Box sx={{ py: { xs: 5, md: 7 }, background: "#050816", overflow: "hidden" }}>
        <Container maxWidth="lg">
          <SectionHeading
            title="Train Your Chatbot Using Your Business Data"
            sub="Our AI crawls your website and learns industry-specific content to deliver accurate, context-aware responses."
          />
          <Grid container spacing={6} alignItems="stretch">
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <Box sx={{ width: "100%" }}>
                <FeatureRow
                  items={[
                    {
                      icon: FileText,
                      title: "PDF Upload",
                      desc: "Upload PDFs, documents, and files for your chatbot to learn from.",
                    },
                    {
                      icon: Globe,
                      title: "Website Crawling",
                      desc: "Enter your website URL to auto-crawl and train your chatbot.",
                    },
                    {
                      icon: BookOpen,
                      title: "Knowledge Base",
                      desc: "Train with FAQs, articles, and documentation.",
                    },
                    {
                      icon: Search,
                      title: "AI Search",
                      desc: "Intelligent search across all trained data sources.",
                    },
                    {
                      icon: Brain,
                      title: "Smart Responses",
                      desc: "Generate accurate, context-aware answers from your data.",
                    },
                    {
                      icon: RefreshCw,
                      title: "Continuous Learning",
                      desc: "Bot automatically improves with new content over time.",
                    },
                  ]}
                  cols={{ xs: 12, sm: 6 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                style={{ width: "100%", height: "100%" }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    background:
                      "linear-gradient(160deg, rgba(16,185,129,0.08) 0%, rgba(56,189,248,0.04) 100%)",
                    border: "1px solid rgba(16,185,129,0.2)",
                  }}
                >
                  <Box sx={{ textAlign: "center", mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 800,
                        color: "#fff",
                        letterSpacing: 1,
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      How It Works
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#64748b",
                      }}
                    >
                      Three simple steps to train your AI chatbot
                    </Typography>
                  </Box>

                  <motion.div
                    whileHover={{ scale: 1.02, x: 4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "rgba(16,185,129,0.05)",
                        border: "1px solid rgba(16,185,129,0.12)",
                        borderLeft: "3px solid #10b981",
                        transition: "all 0.3s",
                        "&:hover": {
                          bgcolor: "rgba(16,185,129,0.1)",
                          boxShadow: "0 4px 20px rgba(16,185,129,0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(16,185,129,0.15)",
                          flexShrink: 0,
                        }}
                      >
                        <Globe size={20} strokeWidth={1.5} color="#10b981" />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#fff",
                            mb: 0.5,
                          }}
                        >
                          1. Provide Your Source
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            color: "#94a3b8",
                            mb: 0.5,
                          }}
                        >
                          Upload PDFs, DOCX, or TXT files — or simply paste your
                          website URL to auto-crawl all pages.
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                        >
                          {[
                            "Drag & drop files",
                            "URL crawl",
                            "Batch upload",
                          ].map((tag, ti) => (
                            <Typography
                              key={ti}
                              sx={{
                                fontSize: "0.6rem",
                                color: "#10b981",
                                bgcolor: "rgba(16,185,129,0.1)",
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                              }}
                            >
                              {tag}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>

                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 2,
                        height: 24,
                        bgcolor: "rgba(16,185,129,0.25)",
                      }}
                    />
                  </Box>

                  <motion.div
                    whileHover={{ scale: 1.02, x: 4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "rgba(99,102,241,0.05)",
                        border: "1px solid rgba(99,102,241,0.12)",
                        borderLeft: "3px solid #818cf8",
                        transition: "all 0.3s",
                        "&:hover": {
                          bgcolor: "rgba(99,102,241,0.1)",
                          boxShadow: "0 4px 20px rgba(99,102,241,0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(99,102,241,0.15)",
                          flexShrink: 0,
                        }}
                      >
                        <Brain size={20} strokeWidth={1.5} color="#818cf8" />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#fff",
                            mb: 0.5,
                          }}
                        >
                          2. AI Learns Your Data
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            color: "#94a3b8",
                            mb: 0.5,
                          }}
                        >
                          Our AI crawls every page, indexes your content, and
                          understands your business context and terminology.
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                        >
                          {[
                            "Auto-indexing",
                            "Context analysis",
                            "Industry aware",
                          ].map((tag, ti) => (
                            <Typography
                              key={ti}
                              sx={{
                                fontSize: "0.6rem",
                                color: "#818cf8",
                                bgcolor: "rgba(99,102,241,0.1)",
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                              }}
                            >
                              {tag}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>

                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 2,
                        height: 24,
                        bgcolor: "rgba(99,102,241,0.25)",
                      }}
                    />
                  </Box>

                  <motion.div
                    whileHover={{ scale: 1.02, x: 4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "rgba(56,189,248,0.05)",
                        border: "1px solid rgba(56,189,248,0.12)",
                        borderLeft: "3px solid #38bdf8",
                        transition: "all 0.3s",
                        "&:hover": {
                          bgcolor: "rgba(56,189,248,0.1)",
                          boxShadow: "0 4px 20px rgba(56,189,248,0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(56,189,248,0.15)",
                          flexShrink: 0,
                        }}
                      >
                        <MessageSquare
                          size={20}
                          strokeWidth={1.5}
                          color="#38bdf8"
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#fff",
                            mb: 0.5,
                          }}
                        >
                          3. Delivers Smart Replies
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            color: "#94a3b8",
                            mb: 0.5,
                          }}
                        >
                          Your chatbot delivers accurate, context-aware answers
                          24/7 and seamlessly hands off to humans when needed.
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                        >
                          {[
                            "24/7 response",
                            "Context aware",
                            "Human handoff",
                          ].map((tag, ti) => (
                            <Typography
                              key={ti}
                              sx={{
                                fontSize: "0.6rem",
                                color: "#38bdf8",
                                bgcolor: "rgba(56,189,248,0.1)",
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                              }}
                            >
                              {tag}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 5, md: 7 },
          background: "linear-gradient(180deg, #0a0e27, #050816)",
        }}
      >
        <Container maxWidth="lg">
          <SectionHeading
            title="Launch Faster With Ready-Made Templates"
            sub="Start with professionally designed chatbot templates tailored for your industry."
          />
          <Grid container spacing={3}>
            {[
              {
                industry: "Real Estate",
                color: BRAND.cyan,
                chat: [
                  { side: "bot", text: "Hi! Looking for a property?" },
                  { side: "user", text: "2BHK under 50L" },
                  { side: "bot", text: "Here are 12 options..." },
                ],
              },
              {
                industry: "Healthcare",
                color: "#10b981",
                chat: [
                  { side: "bot", text: "Book an appointment?" },
                  { side: "user", text: "Yes, Dr. Sharma" },
                  { side: "bot", text: "Available at 4PM..." },
                ],
              },
              {
                industry: "Education",
                color: BRAND.purple,
                chat: [
                  { side: "bot", text: "Course inquiries?" },
                  { side: "user", text: "MBA fees?" },
                  { side: "bot", text: "\u20B94.5L/year..." },
                ],
              },
              {
                industry: "Travel",
                color: "#f59e0b",
                chat: [
                  { side: "bot", text: "Where to?" },
                  { side: "user", text: "Goa, 3 nights" },
                  { side: "bot", text: "Best deals from..." },
                ],
              },
              {
                industry: "E-Commerce",
                color: "#ec4899",
                chat: [
                  { side: "bot", text: "Can I help?" },
                  { side: "user", text: "Track order #123" },
                  { side: "bot", text: "Out for delivery!" },
                ],
              },
              {
                industry: "Banking",
                color: BRAND.primary,
                chat: [
                  { side: "bot", text: "Welcome to XYZ Bank" },
                  { side: "user", text: "Check balance" },
                  { side: "bot", text: "Your balance is..." },
                ],
              },
              {
                industry: "Insurance",
                color: "#06b6d4",
                chat: [
                  { side: "bot", text: "Claim assistance?" },
                  { side: "user", text: "File a claim" },
                  { side: "bot", text: "I'll guide you..." },
                ],
              },
              {
                industry: "HR & Recruitment",
                color: "#f97316",
                chat: [
                  { side: "bot", text: "Job openings?" },
                  { side: "user", text: "Software roles" },
                  { side: "bot", text: "5 positions open" },
                ],
              },
              {
                industry: "SaaS",
                color: BRAND.cyan,
                chat: [
                  { side: "bot", text: "Support & sales" },
                  { side: "user", text: "Pricing please" },
                  { side: "bot", text: "Plans start at..." },
                ],
              },
              {
                industry: "Consulting",
                color: BRAND.purple,
                chat: [
                  { side: "bot", text: "Free consultation?" },
                  { side: "user", text: "Schedule a call" },
                  { side: "bot", text: "Pick a slot..." },
                ],
              },
              {
                industry: "Legal",
                color: "#8b5cf6",
                chat: [
                  { side: "bot", text: "Legal assistance?" },
                  { side: "user", text: "Contract review" },
                  { side: "bot", text: "I'll connect you..." },
                ],
              },
              {
                industry: "Food & Restaurant",
                color: "#ef4444",
                chat: [
                  { side: "bot", text: "Order or reserve?" },
                  { side: "user", text: "Show menu" },
                  { side: "bot", text: "Today's specials..." },
                ],
              },
              {
                industry: "Automotive",
                color: "#f97316",
                chat: [
                  { side: "bot", text: "Car inquiry?" },
                  { side: "user", text: "Test drive SUV" },
                  { side: "bot", text: "Book a test drive..." },
                ],
              },
              {
                industry: "Fitness & Wellness",
                color: "#10b981",
                chat: [
                  { side: "bot", text: "Membership info?" },
                  { side: "user", text: "Gym fees?" },
                  { side: "bot", text: "Plans from..." },
                ],
              },
              {
                industry: "Entertainment",
                color: "#ec4899",
                chat: [
                  { side: "bot", text: "What's playing?" },
                  { side: "user", text: "Movie timings" },
                  { side: "bot", text: "Tonight at 7PM..." },
                ],
              },
              {
                industry: "Non-Profit",
                color: "#14b8a6",
                chat: [
                  { side: "bot", text: "Donation inquiry?" },
                  { side: "user", text: "How to donate" },
                  { side: "bot", text: "You can donate..." },
                ],
              },
              {
                industry: "Logistics",
                color: "#f59e0b",
                chat: [
                  { side: "bot", text: "Shipment status?" },
                  { side: "user", text: "Track #TRK789" },
                  { side: "bot", text: "In transit..." },
                ],
              },
              {
                industry: "Telecom",
                color: "#6366f1",
                chat: [
                  { side: "bot", text: "Plan assistance?" },
                  { side: "user", text: "Best prepaid plan" },
                  { side: "bot", text: "\u20B9499 with 2GB..." },
                ],
              },
              {
                industry: "Hospitality",
                color: "#a855f7",
                chat: [
                  { side: "bot", text: "Room booking?" },
                  { side: "user", text: "Suite available?" },
                  { side: "bot", text: "Yes, \u20B94500/night" },
                ],
              },
            ].map((t, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  whileHover={{ y: -4 }}
                  style={{ height: "100%" }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      height: "100%",
                      bgcolor: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      transition: "all 0.3s",
                      "&:hover": {
                        borderColor: `${t.color}44`,
                        boxShadow: `0 8px 30px ${t.color}1a`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        mb: 1.5,
                        bgcolor: "rgba(0,0,0,0.2)",
                      }}
                    >
                      {t.chat.map((msg, mi) => (
                        <Box
                          key={mi}
                          sx={{
                            display: "flex",
                            justifyContent:
                              msg.side === "bot" ? "flex-start" : "flex-end",
                            px: 1.5,
                            py: 0.8,
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: "85%",
                              px: 1.2,
                              py: 0.6,
                              borderRadius:
                                msg.side === "bot"
                                  ? "0 10px 10px 10px"
                                  : "10px 0 10px 10px",
                              bgcolor:
                                msg.side === "bot"
                                  ? "rgba(255,255,255,0.06)"
                                  : `${t.color}22`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#cbd5e1",
                                lineHeight: 1.3,
                              }}
                            >
                              {msg.text}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        color: "#fff",
                        mb: 0.3,
                      }}
                    >
                      {t.industry}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: t.color,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.65rem", color: "#64748b" }}
                      >
                        {t.industry} Template
                      </Typography>
                    </Box>
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => navigate("/signup")}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        py: 0.6,
                        bgcolor: `${t.color}18`,
                        color: t.color,
                        border: `1px solid ${t.color}33`,
                        "&:hover": {
                          bgcolor: `${t.color}30`,
                          borderColor: t.color,
                        },
                      }}
                    >
                      Use {t.industry} Template
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 5, md: 7 }, background: "#050816" }}>
        <Container maxWidth="lg">
          <SectionHeading
            title="Why Businesses Choose TalkToKam"
            sub="Thousands of businesses trust TalkToKam to power their website conversations."
          />
          <Grid container spacing={3}>
            {[
              {
                icon: Zap,
                title: "Easy Setup",
                desc: "Launch your chatbot within minutes.",
                color: BRAND.cyan,
              },
              {
                icon: Bot,
                title: "AI Powered",
                desc: "Deliver intelligent customer interactions.",
                color: BRAND.purple,
              },
              {
                icon: Code,
                title: "No-Code Builder",
                desc: "Build without technical expertise.",
                color: BRAND.primary,
              },
              {
                icon: TrendingUp,
                title: "Scalable",
                desc: "Suitable for startups and enterprises.",
                color: "#10b981",
              },
              {
                icon: Shield,
                title: "Secure",
                desc: "Enterprise-grade security and privacy.",
                color: "#f59e0b",
              },
              {
                icon: MessageCircle,
                title: "Reliable Support",
                desc: "Dedicated customer success assistance.",
                color: "#ec4899",
              },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <GlassCard glowColor={item.color}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: `${item.color}14`,
                        border: `1px solid ${item.color}22`,
                        flexShrink: 0,
                        transition: "all 0.3s",
                        "&:hover": {
                          bgcolor: `${item.color}22`,
                          boxShadow: `0 0 20px ${item.color}22`,
                        },
                      }}
                    >
                      <item.icon size={22} color={item.color} />
                    </Box>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                    {item.desc}
                  </Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 5, md: 7 }, background: "#050816", overflow: "hidden" }}>
        <Container maxWidth="lg">
          <SectionHeading emoji="❓" title="Frequently Asked Questions" />
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {faqData.map((faq, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Accordion
                      expanded={expanded === i}
                      onChange={(_, isExp) => setExpanded(isExp ? i : false)}
                      sx={{
                        bgcolor: `rgba(255,255,255,${expanded === i ? "0.06" : "0.02"})`,
                        borderRadius: "12px !important",
                        border: `1px solid ${expanded === i ? `${BRAND.cyan}44` : "rgba(255,255,255,0.06)"}`,
                        "&:before": { display: "none" },
                        boxShadow:
                          expanded === i ? `0 0 30px ${BRAND.cyan}11` : "none",
                        transition: "all 0.3s",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{
                              color: expanded === i ? BRAND.cyan : "#64748b",
                              transition: "color 0.3s",
                            }}
                          />
                        }
                        sx={{ "& .MuiAccordionSummary-content": { my: 1.2 } }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: `${BRAND.cyan}14`,
                              border: `1px solid ${BRAND.cyan}22`,
                              flexShrink: 0,
                              fontSize: "0.75rem",
                            }}
                          >
                            {expanded === i ? "\u2212" : "+"}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: expanded === i ? 700 : 600,
                              fontSize: "0.95rem",
                              color: expanded === i ? "#fff" : "#e0e7ff",
                              transition: "all 0.3s",
                            }}
                          >
                            {faq.q}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pb: 2, pt: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            color: "#94a3b8",
                            lineHeight: 1.7,
                            pl: 5.5,
                          }}
                        >
                          {faq.a}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </motion.div>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: "flex" }}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                style={{ width: "100%", height: "100%" }}
              >
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, rgba(56,189,248,0.08), rgba(139,92,246,0.08))",
                    border: "1px solid rgba(56,189,248,0.12)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-40%",
                      right: "-40%",
                      width: 280,
                      height: 280,
                      borderRadius: "50%",
                      background:
                        "radial-gradient(circle, rgba(56,189,248,0.08), transparent)",
                      pointerEvents: "none",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-30%",
                      left: "-30%",
                      width: 200,
                      height: 200,
                      borderRadius: "50%",
                      background:
                        "radial-gradient(circle, rgba(139,92,246,0.08), transparent)",
                      pointerEvents: "none",
                    }}
                  />
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${BRAND.cyan}14`,
                      border: `1px solid ${BRAND.cyan}22`,
                      mb: 2,
                    }}
                  >
                    <MessageCircle size={28} color={BRAND.cyan} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.2rem",
                      color: "#fff",
                      mb: 1,
                    }}
                  >
                    Still have questions?
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "#94a3b8",
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    Can't find the answer you're looking for? We're here to
                    help.
                  </Typography>
                  <Box
                    component="a"
                    href="mailto:support@talktokam.com"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      px: 3,
                      py: 1.5,
                      borderRadius: "10px",
                      background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      textDecoration: "none",
                      cursor: "pointer",
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 8px 30px ${BRAND.cyan}33`,
                      },
                    }}
                  >
                    <Mail size={16} />
                    Contact Support
                  </Box>
                  <Box
                    sx={{
                      mt: 2.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#10b981",
                      }}
                    />
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                      Average response time: &lt; 5 mins
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Features;
